import { Button, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

export const BUNDLE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const MIN_TIME_BETWEEN_CHECKS_MS = 60 * 1000;
const TOAST_ID = "new-bundle-available";
const RELOADED_FOR_COMMIT_KEY = "reloaded-for-bundle-commit";

export const fetchDeployedCommit = async (): Promise<string | undefined> => {
  try {
    const response = await fetch("/meta.json", { cache: "no-store" });
    if (!response.ok) return undefined;
    const { commit } = (await response.json()) as { commit?: unknown };
    return typeof commit === "string" && commit.length > 0 ? commit : undefined;
  } catch {
    return undefined;
  }
};

const alreadyReloadedFor = (deployedCommit: string) =>
  sessionStorage.getItem(RELOADED_FOR_COMMIT_KEY) === deployedCommit;

const reloadFor = (deployedCommit: string) => {
  sessionStorage.setItem(RELOADED_FOR_COMMIT_KEY, deployedCommit);
  window.location.reload();
};

interface Props {
  bundleCommit: string | undefined;
}

export const BundleVersionWatcher = ({ bundleCommit }: Props) => {
  const { pathname } = useLocation();
  const toast = useToast();
  const { t } = useTranslation();
  const deployedCommitRef = useRef<string>();
  const lastCheckedAtRef = useRef(0);

  const showReloadToast = useCallback(
    (deployedCommit: string) => {
      if (toast.isActive(TOAST_ID)) return;
      toast({
        id: TOAST_ID,
        status: "info",
        duration: null,
        isClosable: true,
        position: "top-right",
        title: t("A new version of SourceMagnet is available"),
        description: (
          <Button size="sm" mt={2} onClick={() => reloadFor(deployedCommit)}>
            {t("Reload")}
          </Button>
        ),
      });
    },
    [toast, t],
  );

  const reloadOnce = useCallback(
    (deployedCommit: string) => {
      if (alreadyReloadedFor(deployedCommit)) showReloadToast(deployedCommit);
      else reloadFor(deployedCommit);
    },
    [showReloadToast],
  );

  const check = useCallback(
    async (whenStale: "reload" | "notify") => {
      if (!bundleCommit || Date.now() - lastCheckedAtRef.current < MIN_TIME_BETWEEN_CHECKS_MS) return;
      lastCheckedAtRef.current = Date.now();
      const deployedCommit = await fetchDeployedCommit();
      if (!deployedCommit || deployedCommit === bundleCommit) return;
      deployedCommitRef.current = deployedCommit;
      if (whenStale === "reload") reloadOnce(deployedCommit);
      else showReloadToast(deployedCommit);
    },
    [bundleCommit, reloadOnce, showReloadToast],
  );

  useEffect(() => {
    if (deployedCommitRef.current) reloadOnce(deployedCommitRef.current);
    else void check("reload");
  }, [pathname, check, reloadOnce]);

  useEffect(() => {
    if (!bundleCommit) return;
    const notify = () => void check("notify");
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") notify();
    };
    const interval = setInterval(notify, BUNDLE_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [bundleCommit, check]);

  return null;
};
